import { useState, useEffect } from 'react'
import { PhoneCall, PhoneOff, Loader2, WifiOff, AlertTriangle, Mic, MicOff, PhoneForwarded } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useTelemetryStore } from '../../stores/telemetryStore'
import { useActiveCallForPhone } from '../phone/useActiveCallForPhone'
import { useMakeCall, useEndCall } from '../../api/calls'
import { useAssistants } from '../../api/assistants'
import { apiErrorCode } from '../../api/client'
import { useGetMonitorUrls } from '../../api/monitoring'
import { useCallMonitor } from './useCallMonitor'
import { useBargeIn } from './useBargeIn'
import { ListenButton } from './ListenButton'
import { AssistantPicker } from './AssistantPicker'
import { useDirectionLabel } from '../../i18n/statusLabels'
import { cn, elapsedSince, phonesMatch, toE164 } from '../../lib/utils'
import { statusConfig, dotColor } from '../../lib/callStatusConstants'

const ASSISTANT_STORAGE_KEY = 'vapi.selectedAssistantId'

interface CallStatusPanelProps {
  phoneNumber: string
}

export function CallStatusPanel({ phoneNumber }: CallStatusPanelProps) {
  const { t } = useTranslation('calls')
  const activeCall = useActiveCallForPhone(phoneNumber)
  const recentEvents = useTelemetryStore((s) => s.recentEvents)
  const sseConnectionState = useTelemetryStore((s) => s.sseConnectionState)
  const registerPendingCall = useTelemetryStore((s) => s.registerPendingCall)
  const makeCall = useMakeCall()
  const endCall = useEndCall()
  // Same query key as the inner picker — TanStack Query dedupes; we read it
  // here only to gate the call button when the org is unprovisioned.
  const { data: assistantsData } = useAssistants()
  const isUnprovisioned = assistantsData?.reason === 'no_phone_assigned'

  const [elapsed, setElapsed] = useState('')
  const [stuckWarning, setStuckWarning] = useState(false)
  const [selectedAssistantId, setSelectedAssistantId] = useState<string>(() => {
    if (typeof window === 'undefined') return ''
    return window.localStorage.getItem(ASSISTANT_STORAGE_KEY) ?? ''
  })

  const handleAssistantChange = (id: string) => {
    setSelectedAssistantId(id)
    if (typeof window !== 'undefined') {
      if (id) {
        window.localStorage.setItem(ASSISTANT_STORAGE_KEY, id)
      } else {
        window.localStorage.removeItem(ASSISTANT_STORAGE_KEY)
      }
    }
  }

  const status = activeCall?.status
  const isCallActive = !!activeCall && status !== 'ended'

  // Fetch monitor URLs when we have an active call (fallback if SSE didn't provide them)
  const monitorQuery = useGetMonitorUrls(isCallActive ? activeCall?.call_sid : undefined)
  const listenUrl = activeCall?.listen_url || monitorQuery.data?.listen_url

  // Call monitoring (listen to live audio) — user opts in via the Listen button.
  const monitor = useCallMonitor({ listenUrl, enabled: false })

  // Barge-in (jump into call via browser mic)
  const bargeIn = useBargeIn()

  const directionLabel = useDirectionLabel(activeCall?.direction ?? '')

  // Elapsed timer
  useEffect(() => {
    if (!activeCall || activeCall.status === 'ended') return
    const interval = setInterval(() => {
      setElapsed(elapsedSince(activeCall.started_at))
    }, 1000)
    // FOLLOW-UP(react-compiler): local state synced from the SSE telemetry store.
    // It has multiple writers (this effect plus user event handlers), so deriving
    // it during render is a state-machine redesign, not a mechanical fix — and the
    // UI has no JS test suite to catch a regression. Suppressed, not fixed.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setElapsed(elapsedSince(activeCall.started_at))
    return () => clearInterval(interval)
  }, [activeCall])

  // Stuck-in-dialing detection: warn after 20s in 'initiated' with no progress
  useEffect(() => {
    // FOLLOW-UP(react-compiler): local state synced from the SSE telemetry store.
    // It has multiple writers (this effect plus user event handlers), so deriving
    // it during render is a state-machine redesign, not a mechanical fix — and the
    // UI has no JS test suite to catch a regression. Suppressed, not fixed.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStuckWarning(false)
    if (!activeCall || activeCall.status !== 'initiated') return
    const timer = setTimeout(() => setStuckWarning(true), 20_000)
    return () => clearTimeout(timer)
  }, [activeCall?.call_sid, activeCall?.status])

  // Check for ended info from recent events when no active call
  const endedEvent = !activeCall
    ? recentEvents.find(
        (e) =>
          e.event_type === 'call.ended' &&
          (phonesMatch(e.payload.from_number || '', phoneNumber) || phonesMatch(e.payload.to_number || '', phoneNumber)),
      )
    : undefined

  const handleMakeCall = () => {
    const e164Phone = toE164(phoneNumber)
    makeCall.mutate(
      { phone: e164Phone, assistant_id: selectedAssistantId || undefined },
      {
        onSuccess: (data) => {
          if (data?.call_id) {
            // Prefer the provider tag from the backend response if it
            // exposes one; fall back to 'vapi' (the legacy default).
            // Once Phase 2's CallEvent ``mode`` is also exposed on the
            // HTTP response body, this picks it up without further UI
            // changes.
            registerPendingCall(data.call_id, phoneNumber, data.mode ?? 'vapi')
          }
        },
      },
    )
  }

  const handleEndCall = () => {
    if (!activeCall) return
    endCall.mutate(activeCall.call_sid)
  }

  const handleBargeIn = () => {
    if (!activeCall) return
    bargeIn.bargeIn(activeCall.call_sid)
  }

  const visual = status ? statusConfig[status] : undefined
  const statusLabel = status ? t(`lifecycle.${status}`) : undefined

  return (
    <div className="rounded-lg border border-border bg-surface-card p-5">
      {/* Status indicator */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {visual && status ? (
            <span
              className={cn(
                'inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium',
                visual.color,
              )}
            >
              <span
                className={cn(
                  'h-2 w-2 rounded-full',
                  dotColor[status],
                  visual.pulse && 'animate-pulse',
                )}
              />
              {statusLabel}
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 rounded-full bg-surface-elevated px-3 py-1 text-sm font-medium text-text-muted">
              <span className="h-2 w-2 rounded-full bg-text-muted" />
              {t('lifecycle.ready')}
            </span>
          )}

          {isCallActive && elapsed && (
            <span className="font-mono text-sm text-text-secondary">{elapsed}</span>
          )}
        </div>

        {activeCall && status !== 'ended' && (
          <div className="flex items-center gap-2 text-xs text-text-muted">
            <span>{directionLabel}</span>
            <span>·</span>
            <span>{activeCall.mode}</span>
          </div>
        )}
      </div>

      {/* Assistant picker */}
      <div className="mt-4">
        <AssistantPicker
          value={selectedAssistantId}
          onChange={handleAssistantChange}
          disabled={isCallActive || makeCall.isPending}
        />
      </div>

      {/* Action buttons */}
      <div className="mt-3 flex items-center gap-3">
        <button
          onClick={handleMakeCall}
          disabled={isCallActive || makeCall.isPending || isUnprovisioned}
          className={cn(
            'inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors',
            isCallActive || makeCall.isPending || isUnprovisioned
              ? 'cursor-not-allowed bg-surface-elevated text-text-muted'
              : 'bg-success text-white hover:bg-success/80',
          )}
        >
          {makeCall.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <PhoneCall className="h-4 w-4" />
          )}
          {makeCall.isPending ? t('staging.calling') : t('staging.callNow')}
        </button>

        {isCallActive && (
          <button
            onClick={handleEndCall}
            disabled={endCall.isPending || status === 'cancelling'}
            className={cn(
              'inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors',
              endCall.isPending || status === 'cancelling'
                ? 'cursor-not-allowed bg-surface-elevated text-text-muted'
                : 'bg-error text-white hover:bg-error/80',
            )}
          >
            {endCall.isPending || status === 'cancelling' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PhoneOff className="h-4 w-4" />
            )}
            {endCall.isPending || status === 'cancelling' ? t('staging.cancelling') : t('staging.endCall')}
          </button>
        )}

        {/* Jump In button — only when call is active and not already barged in */}
        {isCallActive && !bargeIn.isConnected && !bargeIn.isConnecting && (
          <button
            onClick={handleBargeIn}
            className="inline-flex items-center gap-2 rounded-lg bg-info px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-info/80"
          >
            <PhoneForwarded className="h-4 w-4" />
            {t('staging.jumpIn')}
          </button>
        )}

        {/* Barge-in connecting state */}
        {bargeIn.isConnecting && (
          <button disabled className="inline-flex items-center gap-2 rounded-lg bg-surface-elevated px-5 py-2.5 text-sm font-medium text-text-muted cursor-not-allowed">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('staging.joining')}
          </button>
        )}
      </div>

      {/* Listen toggle + Barge-in controls */}
      {isCallActive && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <ListenButton
            isListening={monitor.isListening}
            connectionState={monitor.connectionState}
            audioLevel={monitor.audioLevel}
            hasUrl={!!listenUrl}
            onToggle={monitor.toggleListening}
          />

          {/* Barge-in connected controls */}
          {bargeIn.isConnected && (
            <>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-info/15 px-3 py-1 text-xs font-medium text-info">
                <span className="h-2 w-2 rounded-full bg-info animate-pulse" />
                {t('staging.connectedYouAreIn')}
              </span>
              <button
                onClick={bargeIn.toggleMute}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                  bargeIn.isMuted
                    ? 'bg-warning/15 text-warning hover:bg-warning/25'
                    : 'bg-surface-elevated text-text-secondary hover:bg-surface-elevated/80',
                )}
              >
                {bargeIn.isMuted ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
                {bargeIn.isMuted ? t('staging.unmute') : t('staging.mute')}
              </button>
              <button
                onClick={bargeIn.hangUp}
                className="inline-flex items-center gap-1.5 rounded-lg bg-error/15 px-3 py-1.5 text-xs font-medium text-error transition-colors hover:bg-error/25"
              >
                <PhoneOff className="h-3.5 w-3.5" />
                {t('staging.leaveCall')}
              </button>
            </>
          )}
        </div>
      )}

      {/* Ended info */}
      {activeCall?.status === 'ended' && (
        <div className="mt-3 rounded-md bg-surface-elevated px-3 py-2 text-sm text-text-secondary">
          {t('staging.callEnded')}
          {activeCall.ended_by && (
            <> — {t('staging.endedBy', { by: '' })}<span className="font-medium text-text-primary">{activeCall.ended_by}</span></>
          )}
          {activeCall.ended_reason && (
            <span className="text-text-muted"> ({activeCall.ended_reason})</span>
          )}
        </div>
      )}

      {!activeCall && endedEvent && (
        <div className="mt-3 rounded-md bg-surface-elevated px-3 py-2 text-sm text-text-secondary">
          {t('staging.callEnded')}
          {endedEvent.payload.ended_by && (
            <> — {t('staging.endedBy', { by: '' })}<span className="font-medium text-text-primary">{endedEvent.payload.ended_by}</span></>
          )}
          {endedEvent.payload.ended_reason && (
            <span className="text-text-muted"> ({endedEvent.payload.ended_reason})</span>
          )}
        </div>
      )}

      {/* Error messages */}
      {makeCall.isError && (() => {
        // Map the locked per-org gating error codes to friendly copy. Anything
        // else falls through to the generic ``staging.errors.startCall``
        // template. ``apiErrorCode`` is null-safe and returns undefined for
        // non-ApiError throws.
        const code = apiErrorCode(makeCall.error)
        if (code === 'no_phone_assigned') {
          return (
            <p className="mt-2 text-sm text-error">
              {t('assistant.errors.noPhoneAssigned')}
            </p>
          )
        }
        if (code === 'assistant_not_allowed_for_phone') {
          return (
            <p className="mt-2 text-sm text-error">
              {t('assistant.errors.assistantNotAllowed')}
            </p>
          )
        }
        return (
          <p className="mt-2 text-sm text-error">
            {t('staging.errors.startCall', { message: (makeCall.error as Error).message })}
          </p>
        )
      })()}
      {endCall.isError && (
        <p className="mt-2 text-sm text-error">
          {t('staging.errors.endCall', { message: (endCall.error as Error).message })}
        </p>
      )}
      {monitor.error && (
        <p className="mt-2 text-sm text-warning">
          {t('staging.errors.listen', { message: monitor.error })}
        </p>
      )}
      {bargeIn.error && (
        <p className="mt-2 text-sm text-error">
          {t('staging.errors.bargeIn', { message: bargeIn.error })}
        </p>
      )}

      {/* Stuck in dialing warning */}
      {stuckWarning && activeCall?.status === 'initiated' && (
        <div className="mt-3 flex items-center gap-2 rounded-md bg-warning/10 px-3 py-2 text-sm text-warning">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {t('staging.warnings.stuckDialing')}
        </div>
      )}

      {/* SSE connection warning */}
      {sseConnectionState !== 'connected' && (
        <div className="mt-3 flex items-center gap-2 rounded-md bg-warning/10 px-3 py-2 text-sm text-warning">
          <WifiOff className="h-4 w-4 shrink-0" />
          {t('staging.warnings.sseDown')}
        </div>
      )}
    </div>
  )
}
